import type { Patient, Reference } from '@medplum/fhirtypes'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { getReferenceString, resolveId } from '@seen/core/references'
import { keyBy } from 'lodash-es'
import { useMemo } from 'react'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

/*
 * Hook to determine which documents have open review tasks.
 * Returns a Set of document reference strings that have pending DocumentReviewTasks.
 */
export function useDocumentReviewStatus(
  documents: ExtendedDocumentReference[]
): Set<string> {
  const { tasksByDocument } = useDocumentReviewTasks(documents)

  return useMemo(() => new Set(tasksByDocument.keys()), [tasksByDocument])
}

interface UseDocumentReviewTasksResult {
  tasksByDocument: Map<string, DocumentReviewTask>
  allTasks: DocumentReviewTask[]
  taskDocumentFolders: Map<string, string | undefined>
  isLoading: boolean
}

/*
 * Hook that returns document review task details keyed by document reference string.
 * Queries tasks by participant to avoid URL length limits when there are many documents.
 * Also fetches documents for tasks to provide folder info for badge counts.
 */
export function useDocumentReviewTasks(
  documents: ExtendedDocumentReference[]
): UseDocumentReviewTasksResult {
  const participant = useMemo<Reference<Patient> | undefined>(() => {
    const firstDocWithSubject = documents.find((d) => d.subject)
    return firstDocWithSubject?.subject
  }, [documents])

  const documentIdSet = useMemo(
    () => new Set(documents.map((d) => d.id)),
    [documents]
  )

  // Query by participant to avoid URL length issues with many documents
  const { data: allParticipantTasks = [], isLoading: tasksLoading } =
    useSearchResourcePages(
      DocumentReviewTask,
      participant
        ? DocumentReviewTask.byParticipantPending(participant)
        : undefined,
      { query: { enabled: !!participant } }
    )

  const taskDocumentIds = useMemo(
    () =>
      allParticipantTasks
        .map((task) => resolveId(task.document))
        .filter((id): id is string => id !== undefined),
    [allParticipantTasks]
  )

  const { data: taskDocuments = [], isLoading: docsLoading } = useSearch(
    ExtendedDocumentReference,
    taskDocumentIds.length > 0 ? { _id: taskDocumentIds } : undefined,
    { query: { enabled: taskDocumentIds.length > 0 } }
  )

  const taskDocumentFolders = useMemo(
    () => new Map(taskDocuments.map((doc) => [doc.id, doc.folder?.id])),
    [taskDocuments]
  )

  const tasksByDocument = useMemo(() => {
    const tasksForOurDocuments = allParticipantTasks.filter((task) => {
      const docId = resolveId(task.document)
      return docId && documentIdSet.has(docId)
    })
    const obj = keyBy(tasksForOurDocuments, (task) =>
      getReferenceString(task.document)
    )
    return new Map(Object.entries(obj))
  }, [allParticipantTasks, documentIdSet])

  return {
    tasksByDocument,
    allTasks: allParticipantTasks,
    taskDocumentFolders,
    isLoading: tasksLoading || docsLoading,
  }
}
